import { createLogger } from "../../utils";
import { DateRange } from "../../types/DataRange";
import IResponseWithItems from "../../types/IResponse";
import { Base } from "..";

const logger = createLogger({ name: "ExecutorReplenishment" });

/**
 * Executor balance replenishments (manual, terminal, card).
 * Source: `report.getExecutorReplenishmentsReport`.
 */
class ExecutorReplenishment extends Base {
	public static Type = {
		MANUAL: "manual",
		TERMINAL: "terminal",
		CARD: "card",
	} as const;

	public static async getAll(
		params: ExecutorReplenishment.SearchOptions,
	): Promise<ExecutorReplenishment.GetAllResponse | null> {
		try {
			logger.info("[ExecutorReplenishment] getAll params", params);

			const response = await this.request((prpc) =>
				prpc.theirsModel.report.getExecutorReplenishmentsReport(params),
			);

			logger.info("[ExecutorReplenishment] getAll response", response);

			if (!response) return null;

			return {
				...response,
				items: (response.items ?? []).map(this.fromResponse),
			};
		} catch (error) {
			logger.error("[ExecutorReplenishment] getAll error", error);
			return null;
		}
	}

	private static fromResponse(
		data: ExecutorReplenishment.Model,
	): ExecutorReplenishment.Model {
		return {
			...data,
			createdAt: new Date(data.createdAt),
			amount: Number(data.amount ?? 0),
		};
	}
}

declare namespace ExecutorReplenishment {
	type Type =
		(typeof ExecutorReplenishment.Type)[keyof typeof ExecutorReplenishment.Type];

	interface Model {
		id: string;
		type: Type;
		source: string;
		sourceId: number;
		createdAt: Date;
		/** Signed amount: manual deduction is negative. */
		amount: number;
		executor: { id: number; callSign: string } | null;
		taxiServiceId: number | null;
		paymentSystem: {
			id: number;
			name: string | null;
			paymentProvider: string | null;
		} | null;
		/** Terminal transaction identifier. */
		serviceId: string | null;
		notes: string;
		user: { id: number; type: string; fullName: string } | null;
	}

	interface Counters {
		count: number;
		amount: number;
	}

	interface SearchOptions {
		dateRange: DateRange;
		query?: string;
		types?: Type[];
		taxiServiceIds?: number[];
		executorIds?: number[];
		callSigns?: string[];
		limit?: number;
		offset?: number;
	}

	type GetAllResponse = IResponseWithItems<Model> & { counters: Counters };
}

export default ExecutorReplenishment;
